import React from 'react';
import {
  Button,
  Checkbox,
  Dialog, IconButton,
  Pane,
  Select,
  SelectField,
  Text,
  TextInput,
  Table,
  Tooltip,
  TextInputField, Strong, PlusIcon, CrossIcon, MinusIcon, toaster, TimeIcon, Menu
} from 'evergreen-ui';
import TitleWithCross from 'Utils/TitleWithCross';
import { createUseStyles } from 'react-jss';
import { useUpdateReportMutation } from 'Services/api/reportsApi';
import { SOURCE_TYPE_DATASOURCE, SOURCE_TYPE_STATION } from 'Shared/TimeseriesTables/Timeseries.Utils';
import { useSelector } from 'react-redux';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import CornerDialogError from 'Utils/CornerDialogError';
import { VirtualList } from 'Utils/VirtualList';
import { DAY, DECADE, MONTH, WEEK, YEAR } from 'Utils/Pagination/usePagination';
import { StartEndDate, TextInputBlur } from 'Shared';
import {
  getNeededMeteoCodesByDatasourceId, getNeededMeteoCodesByStationId
} from 'Services/api/meteoCodesApi';
import { CheckboxList } from 'Shared/CheckboxList2';
import { useNavigate } from 'react-router-dom';
import getRandomColor from 'Utils/randomColor';
import StationsFilterFull from 'Features/Filters/StationFilterFull';
import { datasourceTypesDictionarySelector, datasourcesSelector, stationsFullSelector } from 'Features/App/Dictionaries/selectors';
import { filteredStationsIdsSelector } from 'Features/Filters/Stations';
import { ReportContext } from './Report.Context';
import {getDatasourcesByStationId} from "./ReportApi";

const useStyles = createUseStyles({
  displayContents: {
    '& > div': {
      display: 'contents',
    },
  },
  hoverable: {
    '&:hover': {
      backgroundColor: '#edf0f2',
    },
  },
});

export default function ReportForm() {
  const {
    reportName, calcModuleParams, isFixedSettings, selectedReportId, groups, date, pageSize,
  } = React.useContext(ReportContext);
  const [updateReport] = useUpdateReportMutation();
  const navigate = useNavigate();

  const [errors, setErrors] = React.useState([]);

  const validate = ({ name, report }) => {
    const err = [];
    if (!name) err.push('Введите название отчета');
    report.groups.forEach((group, groupIndex) => {
      const [post, columns] = group;
      if (!post.name) err.push(`Введите название группы ${groupIndex + 1}`);
      columns.forEach((column, columnIndex) => {
        if (!column.sourceType) err.push(`Выберите тип источника (Группа ${groupIndex + 1}, колонка: ${columnIndex + 1})`);
        if (
            (column.sourceType !== null && column.sourceType === SOURCE_TYPE_STATION)
            && !column.stationDatasourceId
        ) err.push(`Выберите тип источника станции (Группа ${groupIndex + 1}, колонка: ${columnIndex + 1})`);
        if (!column.sourceId) err.push(`Выберите источник (Группа ${groupIndex + 1}, колонка: ${columnIndex + 1})`);
        if (!column.meteocode) err.push(`Выберите метеокод (Группа ${groupIndex + 1}, колонка: ${columnIndex + 1})`);
      });
    });
    return err;
  };

  const submit = async () => {
    const report = {
      id: selectedReportId,
      name: reportName,
      report: {
        groups,
        calcModuleParams,
        isFixedSettings,
      },
    };

    if (isFixedSettings) {
      report.report = {
        ...report.report,
        date,
        pageSize,
      };
    }

    const err = validate(report);
    if (err.length > 0) {
      setErrors(err);
    } else {
      const data = await updateReport({ ...report, report: JSON.stringify(report.report) }).unwrap();
      toaster.success('Сохранено');
      if (!selectedReportId) {
        navigate(`../${data.id}/table`);
      }
    }
  };

  return (
    <Pane display="grid" margin={8} gridTemplateRows="max-content 1fr max-content" rowGap=".5rem">
      <TitleWithCross title="Создание отчета" crossTo=".." />
      <CornerDialogError errors={errors} closeDialog={() => setErrors([])} />
      <Pane display="grid" gridTemplateRows="repeat(2, max-content) 1fr">
        <ReportEditName />
        <ReportFixedSettings />
        <ReportColumnEditor />
      </Pane>
      <Button intent="success" appearance="primary" justifySelf="flex-end" onClick={submit}>Сохранить</Button>
    </Pane>
  );
}

function ReportEditName() {
  const { reportName, setReportName } = React.useContext(ReportContext);

  const onChange = (e) => {
    const { value: changedReportName } = e.target;
    setReportName(changedReportName);
  };

  return (
    <TextInputField
      margin={0}
      value={reportName || ''}
      onChange={onChange}
      label="Название отчета"
    />
  );
}

function ReportFixedSettings() {
  const {
    isFixedSettings, setIsFixedSettings,
    date, setDate,
    pageSize, setPageSize,
    calcModuleParams, setCalcModuleParams,
  } = React.useContext(ReportContext);

  const onChangeDate = ({ name, value }) => {
    setDate((cur) => ({
      ...cur,
      [name]: value,
    }));
  };

  const onChangePageSize = (e) => {
    const { value } = e.target;
    setPageSize(value);
  };

  const onChangeCalcParams = (e) => {
    const { name, checked } = e.target;
    setCalcModuleParams((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (name === 'factData' && checked) {
        const changed = Object.keys(tmp).reduce((acc, item) => {
          if (item === 'factData' || item === 'includeOffGridData') {
            acc[item] = true;
          } else {
            acc[item] = false;
          }
          return acc;
        }, {});
        return changed;
      }
      return {
        ...cur,
        [name]: checked,
      };
    });
  };

  return (
    <Pane>
      <Checkbox
        checked={isFixedSettings}
        name="isFixed"
        label="Фиксированные настройки"
        onChange={(e) => setIsFixedSettings(e.target.checked)}
      />
      <Pane
        display="grid"
        gridAutoFlow="column"
        gridAutoColumns="max-content"
        columnGap=".5em"
        alignItems="center"
        pointerEvents={isFixedSettings ? 'all' : 'none'}
        opacity={isFixedSettings ? '1' : '0.5'}
      >
        <StartEndDate
          startDate={date.startDate}
          endDate={date.endDate}
          updateDate={onChangeDate}
          overriddenStartName="startDate"
          overriddenEndName="endDate"
        />
        <Text> показывать за </Text>
        <Select
          onChange={onChangePageSize}
          value={pageSize}
        >
          <option value={DAY}>День</option>
          <option value={WEEK}>Неделя</option>
          <option value={DECADE}>Декада</option>
          <option value={MONTH}>Месяц</option>
          <option value={YEAR}>Год</option>
        </Select>
        <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".5em">
          <Checkbox
            checked={calcModuleParams.calc}
            label="Расчитать недостающие значения"
            disabled={calcModuleParams.factData}
            name="calc"
            onChange={onChangeCalcParams}
          />
          <Checkbox
            checked={calcModuleParams.round}
            label="Округлить значения"
            disabled={calcModuleParams.factData}
            name="round"
            onChange={onChangeCalcParams}
          />
          <Checkbox
            checked={calcModuleParams.includeOffGridData}
            label="Включить значения вне сетки"
            disabled={calcModuleParams.factData}
            name="includeOffGridData"
            onChange={onChangeCalcParams}
          />
          <Checkbox
            checked={calcModuleParams.factData}
            label="Фактические сроки"
            name="factData"
            onChange={onChangeCalcParams}
          />
        </Pane>
      </Pane>
    </Pane>
  );
}

function ReportColumnEditor() {
  return (
    <Pane display="grid">
      <ReportColumnTableBody />
    </Pane>
  );
}

function ReportColumnTableBody() {
  const {
    groups,
  } = React.useContext(ReportContext);
  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" border>
      <Table>
        <Table.Head>
          <Table.TextHeaderCell>
            <Strong>Блоки отчета</Strong>
          </Table.TextHeaderCell>
        </Table.Head>
      </Table>
      <Pane display="grid" overflow="auto" position="relative">
        <Pane display="grid" gridTemplateColumns="minmax(200px, 200px) 1fr" gridAutoFlow="row" gridAutoRows="max-content" position="absolute" width="100%">
          {
            React.Children.toArray(groups.map((group, index) => (
              <ReportGroup group={group} groupIndex={index} />
            )))
          }
          <AddGroup />
        </Pane>
      </Pane>
    </Pane>
  );
}

function ReportGroup({ group = [{}, []], groupIndex }) {
  const { setGroups } = React.useContext(ReportContext);
  let [meta, columns] = group;

  const changeColor = (value) => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [tmpMeta] = tmp[groupIndex];
      tmpMeta.options.color = value;
      return tmp;
    });
  };

  const changeName = (e) => {
    const { value } = e.target;
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [tmpMeta] = tmp[groupIndex];
      tmpMeta.name = value;
      return tmp;
    });
  };

  const deleteGroup = () => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      tmp.splice(groupIndex, 1);
      return tmp;
    });
  };

  return (
    <>
      <Pane padding={8} borderRight borderBottom display="grid" gridTemplateColumns="1fr max-content" columnGap=".5rem" gridAutoFlow="row" gridAutoRows="max-content">
        <Pane display="grid" gridTemplateColumns="min-content 1fr" alignItems="center" columnGap=".5rem">
          <Text>{`${groupIndex + 1}. `}</Text>
          <TextInputBlur value={meta.name} onChange={changeName} width="100%" />
        </Pane>
        <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".5rem" alignItems="center">
          <ColorPicker defaultColor={meta.options.color} onChange={changeColor} />
          <Tooltip content="Удалить блок">
            <IconButton icon={CrossIcon} intent="danger" appearance="minimal" onClick={deleteGroup} />
          </Tooltip>
        </Pane>
      </Pane>
      <Pane display="grid" gridAutoFlow="row" gridAutoRows="max-content" borderBottom>
        {
          React.Children.toArray(columns.map((column, index) => (
            <ReportColumn2 column={column} groupIndex={groupIndex} columnIndex={index} />
          )))
        }
        <AddColumnButton groupIndex={groupIndex} />
      </Pane>
    </>
  );
}

function SelectStation({ sourceId, sourceType, groupIndex, columnIndex, postName }) {
  const classes = useStyles();
  const [isShown, setIsShown] = React.useState(false);

  return (
    <>
      <Menu.Item padding={8} height="max-content" textAlign="start" lineHeight={1.5} fontSize={12} appearance="minimal" onClick={() => setIsShown(true)}>{postName}</Menu.Item>
      {
        isShown && (
          <Dialog
            isShown
            shouldCloseOnOverlayClick={false}
            onCloseComplete={() => setIsShown(false)}
            hasCancel={false}
            hasFooter={false}
            intent="success"
            confirmLabel="Применить"
            title="Выбор источника"
            width="75%"
            contentContainerProps={{
              className: classes.displayContents,
            }}
          >
            <SelectStationDialog
              sourceId={sourceId}
              sourceType={sourceType}
              groupIndex={groupIndex}
              columnIndex={columnIndex}
              setIsShown={setIsShown}
            />
          </Dialog>
        )
      }
    </>
  );
}

function SelectStationDatasource({ column, groupIndex, columnIndex, postName }) {
  const classes = useStyles();
  const [isShown, setIsShown] = React.useState(false);

  return (
    <>
      <Menu.Item
          padding={8}
          height="max-content"
          textAlign="start"
          lineHeight={1.5}
          fontSize={12}
          appearance="minimal"
          onClick={() => setIsShown(true)}>
          {column.stationDatasourceName ? column.stationDatasourceName : 'Выберите источник данных'}
      </Menu.Item>
      {
        isShown && (
          <Dialog
            isShown
            shouldCloseOnOverlayClick={false}
            onCloseComplete={() => setIsShown(false)}
            hasCancel={false}
            hasFooter={false}
            intent="success"
            confirmLabel="Применить"
            title="Выбор источника станции"
            width="75%"
            contentContainerProps={{
              className: classes.displayContents,
            }}
          >
            <SelectStationDatasourceDialog
              column={column}
              setIsShown={setIsShown}
              groupIndex={groupIndex}
              columnIndex={columnIndex}
            />
          </Dialog>
        )
      }
    </>
  );
}

function SelectStationDialog({ sourceId, sourceType, groupIndex, columnIndex, setIsShown }) {
  const classes = useStyles();
  const { setGroups } = React.useContext(ReportContext);

  const [selected, setSelected] = React.useState([]);
  const stationsFullDictionaries = useSelector(stationsFullSelector);
  const datasources = useSelector(datasourcesSelector);
  const filteredStationsIds = useSelector(filteredStationsIdsSelector);
  const datasourceTypes = useSelector(datasourceTypesDictionarySelector);

  const filteredStations = React.useMemo(() => {
    const posts = [];
    filteredStationsIds.forEach((stationId) => {
      const { station, datasourceIds } = stationsFullDictionaries[stationId];
      posts.push({
        id: station.id,
        idx: station.idx,
        name: station.name,
        sourceType: SOURCE_TYPE_STATION,
        disabled: sourceType === SOURCE_TYPE_STATION && sourceId === station.id,
      });
      if (datasourceIds.length > 0) {
        datasourceIds.forEach((datasourceId) => {
          const datasource = datasources[datasourceId];
          posts.push({
            id: datasource.id,
            name: datasourceTypes[datasource.datasourceTypeId].fullName,
            stationName: station.name,
            sourceType: SOURCE_TYPE_DATASOURCE,
            disabled: sourceType === SOURCE_TYPE_DATASOURCE && sourceId === datasource.id,
          });
        });
      }
    });
    return posts;
  }, [datasourceTypes, datasources, filteredStationsIds, sourceId, sourceType, stationsFullDictionaries]);

  const onConfirm = () => {
    if (selected.length > 0) {
      setGroups((cur) => {
        const tmp = JSON.parse(JSON.stringify(cur));
        tmp[groupIndex][1][columnIndex] = {
          ...tmp[groupIndex][1][columnIndex],
          sourceId: selected[0].id,
          sourceType: selected[0].sourceType,
        };
        return tmp;
      });
    }
    setIsShown(false);
  };

  const backgroundColor = React.useCallback((item) => {
    if (item.id === sourceId && item.sourceType === sourceType) return '#b7b7b7';
    if (item.id === selected[0]?.id) return '#c1eaba';
    if (item.sourceType === SOURCE_TYPE_STATION) return '#f5f6f8';
    return 'transparent';
  }, [selected, sourceId, sourceType]);

  const RowTable = React.useCallback((item) => {
    return (
      <Table.Row
        className={classes.hoverable}
        cursor="pointer"
        pointerEvents={item.disabled ? 'none' : 'all'}
        backgroundColor={backgroundColor(item)}
        onClick={() => setSelected([{ id: item.id, sourceType: item.sourceType, name: item.name, options: { color: null } }])}
      >
        <Table.TextCell>
          <Text>
            {`[${item.idx || item.id}] ${item.name}`}
          </Text>
        </Table.TextCell>
      </Table.Row>
    );
  }, [backgroundColor, classes.hoverable]);

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr max-content" height={600} rowGap=".5rem">
      <StationsFilterFull isShowCreateButton={false} />
      <Pane height="100%" border>
        <VirtualList data={filteredStations} row={RowTable} />
      </Pane>
      <Button onClick={onConfirm} intent="success" appearance="primary" justifySelf="flex-end">Применить</Button>
    </Pane>
  );
}

function SelectStationDatasourceDialog({ column, setIsShown, groupIndex, columnIndex }) {
  const { setGroups } = React.useContext(ReportContext);


  React.useEffect(() => {
    getDatasourcesByStationId(column.sourceId).then(resp => {
      setFilteredStationDatasources(
          Object.entries(resp.data)
              .map((k, v) => {
                return {
                  id:k[0],
                  name:k[1]
                }
              }))
    })
  }, [column])

  const classes = useStyles();

  const [selectedId, setSelectedId] = React.useState(null);
  const [selectedName, setSelectedName] = React.useState(null);
  const [filteredStationDatasources, setFilteredStationDatasources] = React.useState(null);

  const onConfirm = () => {
    if (selectedId !== null && selectedName !== null) {
      setGroups((cur) => {
        const tmp = JSON.parse(JSON.stringify(cur));
        tmp[groupIndex][1][columnIndex] = {
          ...tmp[groupIndex][1][columnIndex],
          stationDatasourceId:selectedId,
          stationDatasourceName:selectedName,
        };
        return tmp;
      });
    }
    setIsShown(false);
  };

  const backgroundColor = React.useCallback((item) => {
    if (item.id === selectedId) return '#c1eaba';
    return 'transparent';
  }, [selectedId,]);

  const RowTable = React.useCallback((item) => {
    return (
      <Table.Row
        className={classes.hoverable}
        cursor="pointer"
        pointerEvents={item.disabled ? 'none' : 'all'}
        backgroundColor={backgroundColor(item)}
        onClick={() => {
          setSelectedId(item.id)
          setSelectedName(item.name)
        }}
      >
        <Table.TextCell>
          <Text>
            {`[${item.id}] ${item.name}`}
          </Text>
        </Table.TextCell>
      </Table.Row>
    );
  }, [classes.hoverable, backgroundColor]);

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr max-content" height={600} rowGap=".5rem">
      <StationsFilterFull isShowCreateButton={false} />
      <Pane height="100%" border>
        <VirtualList data={filteredStationDatasources} row={RowTable} />
      </Pane>
      <Button onClick={onConfirm} intent="success" appearance="primary" justifySelf="flex-end">Применить</Button>
    </Pane>
  );
}

function ReportColumnParameterWrapper({ children }) {
  return (
    <Pane display="grid" height="100%" padding={8} alignItems="center" borderRight>
      {children}
    </Pane>
  );
}

function ReportColumn2({column, groupIndex, columnIndex}) {
  const [addDatasourceField, setAddDatasourceField] = React.useState(false)
  const {setGroups, posts} = React.useContext(ReportContext);

  const [datasourceMeteocodes, setDatasourceMeteocodes] = React.useState([])
  const [isLoadingDatasourceMeteocodes, setIsLoadingDatasourceMeteocodes] = React.useState(false)

  const [stationMeteocodes, setStationMeteocodes] = React.useState([])
  const [isLoadingStationMeteocodes, setIsLoadingStationMeteocodes] = React.useState(false)


  React.useEffect( () => {
    if (column.sourceType !== null && column.sourceId !== null) {
      if(column.sourceType === 'DATASOURCE') {
        setIsLoadingDatasourceMeteocodes(true)
        getNeededMeteoCodesByDatasourceId(column.sourceId).then((resp) => {
          setDatasourceMeteocodes(resp.data)
          setIsLoadingDatasourceMeteocodes(false)
        })
      }

      if (column.sourceType === 'STATION') {
        setIsLoadingStationMeteocodes(true)
        getNeededMeteoCodesByStationId(column.sourceId, column.stationDatasourceId).then((resp) => {
          setStationMeteocodes(resp.data)
          setIsLoadingStationMeteocodes(false)
        })
      }
    }
  },[column])

  const [selectedMetecode, setSelectedMeteocode] = React.useState(null);

  const postName = React.useMemo(() => {
    let name = 'Выберите источник';
    const foundedPost = posts.find((post) => {
      if (column.sourceType === SOURCE_TYPE_DATASOURCE) {
        setAddDatasourceField(false)
        return post.id === column.sourceId && post.stationIdx;
      }

      if (column.sourceType === SOURCE_TYPE_STATION) {
        setAddDatasourceField(true)
        return post.id === column.sourceId && !post.stationIdx;
      }
      return post.id === column.sourceId && !post.stationIdx;
    });
    if (foundedPost) {
      name = `${foundedPost.name}${foundedPost.station ? ` (${foundedPost.station})` : ''}`;
    }
    return name;
  }, [column.sourceId, column.sourceType, posts]);

  const meteocodes = column.sourceType === 'STATION' ? stationMeteocodes : datasourceMeteocodes;

  const formattedMeteocodes = React.useMemo(() => meteocodes.map((meteocode) => ({
    meteoCode: meteocode.meteoCodeDetail.meteoCodeString,
    meteoCodeName: meteocode.meteoCodeDetail.name,
    entities: meteocode.entities,
    steps: meteocode.steps,
  })), [meteocodes]);

  const deleteColumn = () => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [, columns] = tmp[groupIndex];
      columns.splice(columnIndex, 1);
      return tmp;
    });
  };

  const onChangeStationEntity = (e) => {
    const { value } = e.target;
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [, columns] = tmp[groupIndex];
      const foundedEntity = selectedMetecode.entities.find((entity) => entity.stationEntityDTO.id === parseInt(value, 10));
      if (foundedEntity) {
        columns[columnIndex].stationEntity = {
          id: foundedEntity.stationEntityDTO.id,
          entityName: foundedEntity.stationEntityDTO.labelFromParameters,
        };
      } else {
        columns[columnIndex].stationEntity = null;
      }
      return tmp;
    });
  };

  React.useEffect(() => {
    if (column.meteocode) {
      setSelectedMeteocode(formattedMeteocodes.find((meteocode) => meteocode.meteoCode === column.meteocode));
    } else {
      setSelectedMeteocode(null);
    }
  }, [column.meteocode, formattedMeteocodes]);

  return (
    <Pane display="grid" gridTemplateColumns="200px repeat(3, 2fr) repeat(2, max-content)" alignItems="center" borderBottom>
      <ReportColumnParameterWrapper>
        <SelectStation
          sourceId={column.sourceId}
          sourceType={column.sourceType}
          groupIndex={groupIndex}
          columnIndex={columnIndex}
          postName={postName}
        />
      </ReportColumnParameterWrapper>
      {
        addDatasourceField
        ? <ReportColumnParameterWrapper>
          <SelectStationDatasource
            column={column}
            postName={postName}
            groupIndex={groupIndex}
            columnIndex={columnIndex}
          />
        </ReportColumnParameterWrapper> :
        <ReportColumnParameterWrapper></ReportColumnParameterWrapper>
      }
      <ReportColumnParameterWrapper>
        <MeteocodeSelector
          values={formattedMeteocodes}
          selectedMetecode={selectedMetecode}
          groupIndex={groupIndex}
          columnIndex={columnIndex}
          onConfirm={(meteocode) => setSelectedMeteocode(meteocode)}
          isLoading={isLoadingDatasourceMeteocodes || isLoadingStationMeteocodes}
        />
      </ReportColumnParameterWrapper>
      <ReportColumnParameterWrapper>
        <SelectField margin={0} padding={0} label="Объект станции" value={column.stationEntity?.id || -1} onChange={onChangeStationEntity}>
          <option value={-1}>Выберите объект станции</option>
          {
            selectedMetecode
            && selectedMetecode.entities.length > 0
            && React.Children.toArray(selectedMetecode.entities.map((entity) => (
              <option value={entity.stationEntityDTO.id}>{entity.stationEntityDTO.labelFromParameters}</option>
            )))
          }
        </SelectField>
      </ReportColumnParameterWrapper>
      <Intervals2 column={column} columnIndex={columnIndex} groupIndex={groupIndex} />
      <Tooltip content="Удалить колонку">
        <IconButton icon={CrossIcon} intent="danger" appearance="minimal" onClick={deleteColumn} />
      </Tooltip>
    </Pane>
  );
}

const MeteocodeLabel = React.memo(({ value }) => (
  <>
    <Strong>{`[${value.meteoCode}] `}</Strong>
    <Text>{value.meteoCodeName}</Text>
  </>
));
MeteocodeLabel.displayName = 'MeteocodeLabel';

function MeteocodeSelector({ values = [], selectedMetecode, groupIndex, columnIndex, onConfirm, isLoading }) {
  const { setGroups } = React.useContext(ReportContext);
  const classes = useStyles();
  const [selected, setSelected] = React.useState([]);
  const [isShown, setIsShown] = React.useState(false);

  const confirm = () => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [, columns] = tmp[groupIndex];
      columns[columnIndex] = {
        ...columns[columnIndex],
        meteocode: selected[0]?.meteoCode || null,
        stationEntity: null,
        steps: selected[0]?.steps || [],
      };
      return tmp;
    });
    if (onConfirm) {
      onConfirm(selected[0]);
    }
    setIsShown(false);
  };

  React.useEffect(() => {
    if (selectedMetecode) {
      setSelected([selectedMetecode]);
    } else {
      setSelected([]);
    }
  }, [selectedMetecode]);

  if (isLoading) return <SuspenseSpinner size={30} />;

  return (
    <>
      <Menu.Item
        display="grid"
        padding={8}
        height="max-content"
        textAlign="start"
        appearance="minimal"
        onClick={() => setIsShown(true)}
      >
        {selectedMetecode ? (
          <>
            <Strong color="#1070ca" fontSize={12} fontWeight={700}>{`[${selectedMetecode?.meteoCode}] `}</Strong>
            <Text color="#1070ca" fontSize={12} fontWeight={500}>{selectedMetecode?.meteoCodeName}</Text>
          </>
        ) : <Text color="#1070ca" fontSize={12} fontWeight={500}>Выберите метеокод</Text>}
      </Menu.Item>
      {
        isShown && (
          <Dialog
            isShown
            shouldCloseOnOverlayClick={false}
            onConfirm={confirm}
            onCloseComplete={() => setIsShown(false)}
            hasCancel={false}
            intent="success"
            confirmLabel="Применить"
            title="Выбор метеокода"
            width="75%"
            contentContainerProps={{
              className: classes.displayContents,
            }}
          >
            <CheckboxList
              values={values}
              checkedValues={selected}
              setCheckedValues={setSelected}
              labelProp={['meteoCode', 'meteoCodeName']}
              checkProp="meteoCode"
              LabelElement={MeteocodeLabel}
              maxChecked={1}
            />
          </Dialog>
        )
      }
    </>

  );
}

const defaultColumn = {
  sourceType: null,
  sourceId: null,
  meteocode: null,
  stationEntity: null,
  steps: [],
};

function AddGroup() {
  const { setGroups } = React.useContext(ReportContext);
  const addGroup = () => {
    const color = getRandomColor();
    setGroups((cur) => [...cur, [{ options: { color }, name: '' }, []]]);
  };
  return (
    <Button
      iconAfter={PlusIcon}
      appearance="minimal"
      onClick={addGroup}
      justifySelf="flex-center"
      justifyContent="center"
      intent="success"
      fontSize={14}
    >
      Добавить блок

    </Button>
  );
}

function AddColumnButton({ groupIndex, disabled }) {
  const { setGroups } = React.useContext(ReportContext);
  const addColumn = () => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [, columns] = tmp[groupIndex];
      columns.push(defaultColumn);
      return tmp;
    });
  };
  return (
    <Button
      iconAfter={PlusIcon}
      disabled={disabled}
      intent="success"
      appearance="minimal"
      justifyContent="center"
      onClick={addColumn}
      width="100%"
      fontSize={14}
    >
      Добавить новую колонку
    </Button>
  );
}

function ColorPicker({ width = '25px', onChange, resetOnDblClick = () => { }, defaultColor = 'black' }) {
  const [color, setColor] = React.useState(defaultColor);
  const onChangeColorBlur = (e) => {
    setColor(e.target.value);
    onChange(e.target.value);
  };

  React.useEffect(() => {
    setColor(defaultColor);
  }, [defaultColor]);

  return <input key={color} type="color" style={{ width, border: 'none' }} onBlur={onChangeColorBlur} defaultValue={color} onDoubleClick={resetOnDblClick} />;
}

const PostLabel = React.memo(({ value }) => <Text>{`[${value.stationIdx || value.idx}] ${value.name}`}</Text>);
PostLabel.displayName = 'PostLabel';

function Intervals2({ column, groupIndex, columnIndex }) {
  const classes = useStyles();
  const [isShown, setIsShown] = React.useState(false);

  return (
    <>

      <Tooltip content="Интервалы">
        <IconButton icon={TimeIcon} appearance="minimal" intent="success" onClick={() => setIsShown(true)} />
      </Tooltip>
      {
        isShown && (
          <Dialog
            isShown
            shouldCloseOnOverlayClick={false}
            onCloseComplete={() => setIsShown(false)}
            hasCancel={false}
            intent="success"
            hasFooter={false}
            title="Настройка интервалов"
            width="75%"
            contentContainerProps={{
              className: classes.displayContents,
            }}
          >
            <ReportMeteocodeSetting
              column={column}
              groupIndex={groupIndex}
              columnIndex={columnIndex}
              closeDialog={() => setIsShown(false)}
            />
          </Dialog>
        )
      }
    </>

  );
}

function ReportMeteocodeSetting({ column, groupIndex, columnIndex, closeDialog }) {
  const { setGroups } = React.useContext(ReportContext);

  const [intervals, setIntervals] = React.useState(column.steps);

  const onConfirm = () => {
    setGroups((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const [, columns] = tmp[groupIndex];
      columns[columnIndex].steps = intervals;
      return tmp;
    });
    closeDialog();
  };

  const addInterval = () => {
    setIntervals((cur) => [...cur, { interval: 0, mandatory: false, timeShift: 0 }]);
  };

  const deleteInterval = (index) => () => {
    setIntervals((cur) => [...cur.slice(0, index), ...cur.slice(index + 1)]);
  };

  const onChangeInterval = (index) => (e) => {
    const { name, value, checked, type } = e.target;
    let newValue = value;
    if (type === 'checkbox') {
      newValue = checked;
    }
    setIntervals((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      tmp[index][name] = newValue;
      return tmp;
    });
  };

  return (
    <Pane display="grid" gridTemplateRows="1fr max-content" rowGap=".5rem">
      <Pane border>
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Интервал, м</Table.TextHeaderCell>
            <Table.TextHeaderCell>Смещение относительно &apos;0&apos; суток, м</Table.TextHeaderCell>
            <Table.TextHeaderCell>Обязательно</Table.TextHeaderCell>
            <Table.HeaderCell justifyContent="flex-end">
              <IconButton icon={PlusIcon} disabled={intervals.length >= 5} intent="success" onClick={addInterval} appearance="primary" />
            </Table.HeaderCell>
          </Table.Head>
          <Table.Body overflowY="scroll">
            {
              intervals.length > 0 ? React.Children.toArray(intervals.map(({ interval, mandatory, timeShift }, index) => (
                <Table.Row>
                  <Table.Cell>
                    <TextInput name="interval" value={interval} onChange={onChangeInterval(index)} />
                  </Table.Cell>
                  <Table.Cell>
                    <TextInput name="timeShift" value={timeShift} onChange={onChangeInterval(index)} />
                  </Table.Cell>
                  <Table.Cell>
                    <Checkbox name="mandatory" checked={mandatory} onChange={onChangeInterval(index)} />
                  </Table.Cell>
                  <Table.Cell justifyContent="flex-end">
                    <IconButton icon={MinusIcon} intent="danger" appearance="primary" onClick={deleteInterval(index)} />
                  </Table.Cell>
                </Table.Row>
              ))) : <Table.Row><Table.TextCell>Добавте интервалы</Table.TextCell></Table.Row>
            }
          </Table.Body>
        </Table>
      </Pane>
      <Button justifySelf="flex-end" onClick={onConfirm}>Применить</Button>
    </Pane>
  );
}
